import React, { useState, useMemo } from 'react';
import { MatterDetailed } from '../../../../types/matters';
import { useCalendarStore } from '../../../calendar/store/calendarStore';
import FullCalendarView from '../../../calendar/components/FullCalendarView';
import EventForm from '../../../calendar/components/EventForm';
import EventDetailModal from '../../../calendar/components/EventDetailModal';
import MatterCalendarSidebar from './MatterCalendarSidebar';
import { CalendarDataProvider } from '../../../calendar/components/CalendarDataProvider';
import type { CalendarEvent } from '../../../calendar/types/calendar.types';
import { CalendarIcon, PlusIcon } from '@heroicons/react/24/outline';

interface CalendarTabProps {
  matter: MatterDetailed;
}

const CalendarTab: React.FC<CalendarTabProps> = ({ matter }) => {
  const { 
    events,
    isEventFormOpen, 
    setEventFormOpen
  } = useCalendarStore();

  // Local state for matter-specific event management
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [slotSelection, setSlotSelection] = useState<{ start: Date; end: Date } | null>(null);
  
  // Matter-specific event filtering state
  const [matterEventTypeFilters, setMatterEventTypeFilters] = useState<string[]>([]);
  const [matterPriorityFilters, setMatterPriorityFilters] = useState<string[]>([]);

  // Filter events for this specific matter
  const matterEvents = useMemo(() => {
    let filtered = events.filter(event => event.matterId === matter.id);
    
    // Apply matter-specific event type filters
    if (matterEventTypeFilters.length > 0) {
      filtered = filtered.filter(event => 
        matterEventTypeFilters.includes(event.eventTypeId || 'meeting')
      );
    }
    
    // Apply matter-specific priority filters
    if (matterPriorityFilters.length > 0) {
      filtered = filtered.filter(event => 
        matterPriorityFilters.includes(event.priority || 'medium')
      );
    }
    
    console.log(`📁 [CalendarTab] Matter events for ${matter.title}:`, {
      totalEvents: events.length,
      baseMatterEvents: events.filter(event => event.matterId === matter.id).length,
      filteredMatterEvents: filtered.length,
      matterId: matter.id,
      activeEventTypeFilters: matterEventTypeFilters,
      activePriorityFilters: matterPriorityFilters,
      eventDetails: filtered.map(e => ({ id: e.id, title: e.title, eventTypeId: e.eventTypeId, priority: e.priority }))
    });
    
    return filtered;
  }, [events, matter.id, matter.title, matterEventTypeFilters, matterPriorityFilters]);

  // Create matter-specific event filters that only apply to this matter's events
  const matterEventFilters = useMemo(() => {
    return {
      dateRange: null,
      priorities: matterPriorityFilters,
      eventTypes: matterEventTypeFilters,
      attendees: []
    };
  }, [matterEventTypeFilters, matterPriorityFilters]);

  // Handle event click - show detail modal
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  // Handle slot selection for new event creation
  const handleSlotSelect = (slotInfo: { start: Date; end: Date; slots: Date[] }) => {
    setSlotSelection({ start: slotInfo.start, end: slotInfo.end });
    setEventFormOpen(true);
  };

  // Handle new event button click
  const handleNewEventClick = () => {
    setSlotSelection(null);
    setEditingEvent(null);  // Clear editing state for new event
    setEventFormOpen(true);
  };

  // Handle close event form
  const handleCloseEventForm = () => {
    setEventFormOpen(false);
    setSlotSelection(null);
    setEditingEvent(null);  // Clear editing state
  };

  // Handle close event detail modal
  const handleCloseEventDetail = () => {
    setSelectedEvent(null);
  };

  // Handle edit event from detail modal
  const handleEditEvent = () => {
    if (selectedEvent) {
      setEditingEvent(selectedEvent);  // Store event for editing
      setSelectedEvent(null);          // Close detail modal
      setEventFormOpen(true);          // Open edit form
    }
  };

  // Handle back to matter from event detail modal
  const handleBackToMatter = () => {
    setSelectedEvent(null);
    // The user is already in the matter context, so just close the modal
    // and they'll see the matter calendar view
  };

  // Handle matter-specific event type filtering
  const toggleMatterEventType = (eventTypeId: string) => {
    setMatterEventTypeFilters(prev => 
      prev.includes(eventTypeId) 
        ? prev.filter(id => id !== eventTypeId)
        : [...prev, eventTypeId]
    );
  };

  // Handle matter-specific priority filtering
  const toggleMatterPriority = (priority: string) => {
    setMatterPriorityFilters(prev => 
      prev.includes(priority) 
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  // Clear all matter filters
  const clearMatterFilters = () => {
    setMatterEventTypeFilters([]);
    setMatterPriorityFilters([]);
  };

  return (
    <CalendarDataProvider>
      <div className="h-full flex">
        {/* Matter Calendar Sidebar */}
        <MatterCalendarSidebar
          matter={matter}
          matterEvents={matterEvents}
          matterEventTypeFilters={matterEventTypeFilters}
          matterPriorityFilters={matterPriorityFilters}
          onToggleEventType={toggleMatterEventType}
          onTogglePriority={toggleMatterPriority}
          onClearFilters={clearMatterFilters}
          onCreateEvent={handleNewEventClick}
        />

        {/* Main Calendar Area */}
        <div className="flex-1 flex flex-col">
          {/* Calendar Container - Full Height */}
          <div className="flex-1 w-full">
            {matterEvents.length > 0 ? (
              <FullCalendarView
                onEventClick={handleEventClick}
                onSlotSelect={handleSlotSelect}
                events={matterEvents} // Pass only matter-specific events
                eventFilters={matterEventFilters} // Pass matter-specific filters
                containerMode={true} // Use container mode for matter calendar
              />
            ) : (
              /* Empty State */
              <div className="h-full flex items-center justify-center">
                <div className="text-center py-12">
                  <CalendarIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Events Yet
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
                    No calendar events have been created for this engagement yet.
                    Create your first event to start tracking important dates and deadlines.
                  </p>
                  <button
                    onClick={handleNewEventClick}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-md font-medium transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create First Event
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Form Modal - with matter context */}
        {isEventFormOpen && (
          <EventForm
            isOpen={isEventFormOpen}
            onClose={handleCloseEventForm}
            event={editingEvent || undefined}  // Pass editing event for edit mode
            initialStart={slotSelection?.start}
            initialEnd={slotSelection?.end}
            matterId={matter.id} // Pass matter ID to automatically link events
          />
        )}

        {/* Event Detail Modal */}
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            isOpen={!!selectedEvent}
            onClose={handleCloseEventDetail}
            onEdit={handleEditEvent}
            matter={matter}
            onBackToMatter={handleBackToMatter}
          />
        )}

      </div>
    </CalendarDataProvider>
  );
};

export default CalendarTab; 
